import { Component, Input, forwardRef, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AutoNumberingService } from './services/auto-numbering.service';
import { NumberingConfig } from './models/numbering-config.interface';
import {
  NumberingElement,
  DropdownElementConfig,
  SequenceElementConfig,
  YearElementConfig,
  MonthElementConfig,
  TextElementConfig,
  FormElementConfig
} from './models/numbering-element.interface';
import { TranslatePipe } from '../../../pipes/translate.pipe';

@Component({
  selector: 'app-form-auto-numbering',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './form-auto-numbering.component.html',
  styleUrls: ['./form-auto-numbering.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormAutoNumberingComponent),
      multi: true
    }
  ]
})
export class FormAutoNumberingComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() placeholder: string = '';
  @Input() required: boolean = false;
  @Input() readonly: boolean = false;
  @Input() config: NumberingConfig = { elements: [] };
  @Input() formData: { [key: string]: any } = {}; // 新增：接收完整表單資料

  value: string = '';
  displayValue: string = '';
  dropdownValues: { [key: string]: string } = {};
  dropdownElements: NumberingElement[] = [];
  errorMessage: string = '';
  generatingNumber: boolean = false;

  private destroy$ = new Subject<void>();
  private onChange = (value: string) => {};
  private onTouched = () => {};

  constructor(private autoNumberingService: AutoNumberingService) {}

  ngOnInit(): void {
    this.initializeComponent();
    
    // 監聽 formData 變更
    if (this.formData) {
      this.watchFormDataChanges();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    try {
      console.log('[DEBUG] ⭐ FormAutoNumberingComponent ngOnChanges called!', changes);
      
      // 監聽 formData 變更
      if (changes['formData'] && !changes['formData'].firstChange) {
        console.log('[DEBUG] FormAutoNumberingComponent formData changed:', changes['formData'].currentValue);
        // 防止過度頻繁的重新生成
        setTimeout(() => {
          this.generateNumber();
        }, 50);
      }
      
      // 監聽 config 變更
      if (changes['config'] && !changes['config'].firstChange) {
        this.initializeComponent();
      }
    } catch (error) {
      console.error('[ERROR] FormAutoNumberingComponent ngOnChanges 發生錯誤:', error);
      this.errorMessage = '自動編號更新失敗';
    }
  }

  private initializeComponent(): void {
    // 驗證配置
    const validation = this.autoNumberingService.validateConfig(this.config);
    if (!validation.isValid) {
      this.errorMessage = validation.error || '配置錯誤';
      return;
    }

    // 篩選出下拉選單元素
    this.dropdownElements = this.config.elements.filter(element => element.type === 'dropdown');

    // 如果沒有下拉選單，直接生成編號
    if (this.dropdownElements.length === 0) {
      this.generateNumber();
    }
  }

  private async generateNumber(): Promise<void> {
    try {
      console.log('[DEBUG] 🚀 generateNumber called!');
      console.log('[DEBUG] canGenerate:', this.canGenerate);
      console.log('[DEBUG] formData:', this.formData);
      console.log('[DEBUG] config:', this.config);
      
      if (!this.canGenerate) {
        console.log('[DEBUG] Cannot generate - missing requirements');
        return;
      }

      // 防止重複生成
      if (this.generatingNumber) {
        console.log('[DEBUG] Already generating, skipping');
        return;
      }

      this.generatingNumber = true;
      this.errorMessage = '';

      const result = await this.autoNumberingService.generateNumber(this.config, this.dropdownValues, this.formData || {});

      if (result.success) {
        this.value = result.number;
        this.displayValue = result.number;
        this.onChange(this.value);
        console.log('[DEBUG] 自動編號生成成功:', result.number);
      } else {
        this.errorMessage = result.error || '編號生成失敗';
        console.warn('[WARN] 自動編號生成失敗:', result.error);
      }
    } catch (error) {
      console.error('[ERROR] generateNumber 發生錯誤:', error);
      this.errorMessage = error instanceof Error ? error.message : '編號生成失敗';
    } finally {
      this.generatingNumber = false;
    }
  }

  regenerateNumber(): void {
    this.generateNumber();
  }

  get canGenerate(): boolean {
    console.log('[DEBUG] 🔍 Checking canGenerate...');
    
    // 檢查所有下拉選單是否都已選擇
    for (const element of this.dropdownElements) {
      const key = `dropdown_${element.order}`;
      if (!this.dropdownValues[key]) {
        console.log('[DEBUG] Missing dropdown value for:', key);
        return false;
      }
    }
    
    // 檢查自動編號配置中用到的 form-element 類型元件是否都已有值
    const formElements = this.config.elements.filter(element => element.type === 'form-element');
    for (const element of formElements) {
      const formElementConfig = element.config as FormElementConfig;
      const componentId = formElementConfig.componentId;
      
      if (!componentId) {
        console.log('[DEBUG] 自動編號配置中的 form-element 缺少 componentId');
        continue; // 跳過沒有設定 componentId 的元件
      }
      
      console.log('[DEBUG] 檢查自動編號依賴的元件:', componentId);
      
      const componentData = this.formData?.[componentId];
      if (!componentData) {
        console.log('[DEBUG] 自動編號依賴的元件尚未有資料:', componentId);
        return false;
      }
      
      // 檢查是否有實際的值（不只是空對象）
      if (typeof componentData === 'object') {
        // 對於 radio button 等元件，檢查是否有 value
        if (!componentData.value && componentData.value !== 0 && componentData.value !== false) {
          console.log('[DEBUG] 自動編號依賴的元件尚未選擇值:', componentId);
          return false;
        }
      }
      
      console.log('[DEBUG] ✓ 元件已準備好:', componentId, componentData);
    }
    
    console.log('[DEBUG] ✅ All requirements met, can generate');
    return true;
  }

  onDropdownChange(): void {
    if (this.canGenerate) {
      this.generateNumber();
    }
  }

  // 取得下拉選單標籤
  getDropdownLabel(element: NumberingElement, index: number): string {
    const config = element.config as DropdownElementConfig;
    return config.label || `選項 ${index + 1}`;
  }

  // 取得下拉選單佔位符
  getDropdownPlaceholder(element: NumberingElement): string {
    const config = element.config as DropdownElementConfig;
    return config.placeholder || '請選擇';
  }

  // 取得下拉選單選項
  getDropdownOptions(element: NumberingElement): Array<{label: string, value: string}> {
    const config = element.config as DropdownElementConfig;
    return config.options || [];
  }

  // ControlValueAccessor 實作
  writeValue(value: any): void {
    console.log('[DEBUG] FormAutoNumberingComponent writeValue:', value);
    
    if (value) {
      // 處理動態表單編輯模式的巢狀結構
      if (typeof value === 'object') {
        // 處理巢狀結構 { type: 'auto-numbering', value: {...} }
        if (value.type === 'auto-numbering' && value.value) {
          // 從 value.value 中提取編號
          if (value.value.originalValue) {
            this.value = value.value.originalValue;
          } else if (value.value.number) {
            this.value = value.value.number;
          } else if (typeof value.value === 'string') {
            this.value = value.value;
          } else {
            this.value = '';
          }
        }
        // 處理簡單結構 { originalValue: ... } 或 { number: ... }
        else if (value.originalValue) {
          this.value = value.originalValue;
        } else if (value.number) {
          this.value = value.number;
        } else {
          // 如果是其他物件格式，嘗試轉換為字串
          this.value = JSON.stringify(value);
        }
      } else {
        // 如果已經是字串，直接使用
        this.value = String(value);
      }
    } else {
      this.value = '';
    }
    
    this.displayValue = this.value;
    console.log('[DEBUG] FormAutoNumberingComponent processed value:', this.value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // 處理禁用狀態
  }

  /**
   * 監聽表單資料變更（備用方法，主要還是靠 ngOnChanges）
   */
  private watchFormDataChanges(): void {
    // 這個方法主要是為了未來可能的需求，目前主要使用 ngOnChanges
    console.log('[DEBUG] watchFormDataChanges initialized');
  }
}
